import { Injectable } from '@angular/core';

/**
 * A singleton service that stores a return route to enable "back" navigation
 * across multi-step flows.
 *
 * The typical usage pattern is:
 * 1. A list/summary page (e.g. Service Fulfillment, Orders) sets the return route
 *    before navigating into an application flow via a cell renderer or action button.
 * 2. The application page's cancel/save-and-exit action consumes the return route
 *    and navigates back to it, falling back to the Dashboard if none was stored.
 *
 * The `consume()` method is preferred over `get()` for navigation actions because
 * it clears the stored route after reading, preventing stale routes from affecting
 * subsequent navigations.
 */
@Injectable({
    providedIn: 'root',
})
export class ReturnRouteService {
    private returnRoute: string | null = null;

    /**
     * Stores the route that the user should be returned to after completing or
     * cancelling an application flow.
     * @param route The URL path to store, or null to clear the stored route.
     */
    set(route: string | null): void {
        this.returnRoute = route;
    }

    /**
     * Returns the currently stored return route without clearing it.
     * Prefer `consume()` for navigation actions.
     */
    get(): string | null {
        return this.returnRoute;
    }

    /**
     * Returns the currently stored return route and clears it in one operation.
     * Use this in cancel/save-and-exit handlers to ensure the route is only
     * consumed once and does not affect subsequent navigations.
     * @returns The stored route, or null if none was set.
     */
    consume(): string | null {
        const route = this.returnRoute;
        this.returnRoute = null;
        return route;
    }
}
